import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Button } from 'reactstrap';

import { unlinkEpic } from 'Redux/ducks/auth';
import MessageBox from 'Components/Utility/MessageBox';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import { revealKey } from 'Redux/ducks/orders';
import { getUrl } from 'Redux/ducks/epic';
import 'Components/Order/EpicActivate.scss';

const messages = defineMessages({
  error: {
    id: 'epic-activate.error',
    defaultMessage: 'There was a problem activating your game, please try again or contact support',
  },
});

function EpicActivate({
  location,
  game,
  orderStatus,
  _id,
  oid,
  emailConfirmed,
  bid,
  url,
  epic: userEpic,
  unlinkEpic: propUnlinkEpic,
}) {
  const [connecting, setConnecting] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [message, setMessage] = useState(null);

  const intl = useIntl();

  const saveRedirect = () => {
    localStorage.setItem('epicRedirect', location.pathname);
  };

  const reveal = async () => {
    const { key } = await revealKey(oid, bid, game);
    setRevealing(false);

    if (key === 'error') {
      alert(intl.formatMessage(messages.error)); // eslint-disable-line no-alert
      return false;
    }

    if (key === 'email') {
      return false;
    }

    return window.location.reload(true);
  };

  useEffect(() => {
    if (getUrlParameter('message')) {
      setMessage(getUrlParameter('message'));
    }

    if (url === '') {
      getUrl();
    }
  }, [url, location]);

  if (!_id) {
    return false;
  }

  if (!url) {
    return false;
  }

  if (!emailConfirmed) {
    return (
      <div className="btn btn-secondary btn-block disabled">
        <FormattedMessage id="epic-activate.verify-email" defaultMessage="Please verify your email address to redeem" />
      </div>
    );
  }

  if (orderStatus !== 'COMPLETE') {
    return false;
  }

  if (game.status === 'revealed') {
    return (
      <div className="key-activated-externally">
        <FormattedMessage
          id="epic-activate.activated-epic"
          defaultMessage="Activated on Epic Games {epicUsername}"
          values={{
            epicUsername: game.epicUsername,
          }}
        />
      </div>
    );
  }

  if (userEpic && !isEmpty(userEpic)) {
    return (
      <React.Fragment>
        <Button
          block
          color="secondary"
          onClick={() => {
            setRevealing(true);
            reveal();
          }}
        >
          <div className="epic-btn-icon">
            <img
              loading="lazy"
              src="https://cdn.fanatical.com/production/icons/drm/light/epicgames.svg"
              alt="Epig Games Icon"
              width="100%"
              height="100%"
            />
          </div>
          {!revealing &&
            <FormattedMessage id="epic-reveal.activate" defaultMessage="Activate on Epic Games with account: {accountName}" values={{ accountName: userEpic.displayName }} tagName="span" />
          }
          {revealing &&
            <div className="w-100">
              <FontAwesomeIcon spin icon={faSpinner} className="mr-2" />
              <b>
                <FormattedMessage id="epic-reveal.activating" defaultMessage="Activating" />
              </b>
            </div>
          }
        </Button>
        <p className="m-0">
          <FormattedMessage id="epic-reveal.connected" defaultMessage="Connected to " />
          <b>{userEpic.displayName}</b>&nbsp;
          (
          <button type="button" className="btn btn-link epic-unlink" onClick={propUnlinkEpic}>
            <FormattedMessage id="epic-reveal.change" defaultMessage="Use a different account" />
          </button>
          )
        </p>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Button
        block
        disabled={connecting}
        color="secondary"
        className="epic-connect epic-activate"
        tag={'a'}
        href={url}
        onClick={() => {
          saveRedirect();
          setConnecting(true);
        }}
      >
        {!connecting &&
          <div className="epic-btn-icon">
            <img
              loading="lazy"
              src="https://cdn.fanatical.com/production/icons/drm/light/epicgames.svg"
              alt="Epig Games Icon"
              width="100%"
              height="100%"
            />
          </div>
        }
        {!connecting &&
          <b>
            <FormattedMessage id="epic-reveal.link-account" defaultMessage="Link your Epic Games account to activate" />
          </b>
        }
        {connecting &&
          <div className="w-100">
            <FontAwesomeIcon spin icon={faSpinner} className="mr-2" />
            <b>
              <FormattedMessage id="epic-reveal.connecting" defaultMessage="connecting" />
            </b>
          </div>
        }
      </Button>
      {message &&
        <div className="mt-3">
          <MessageBox
            dismissible
            type="error"
            title="Error"
            message={message}
            supportLink={360007075098}
            closeFunction={() => { setMessage(false); }}
          />
        </div>
      }
    </React.Fragment>
  );
}

EpicActivate.propTypes = {
  _id: PropTypes.string,
  emailConfirmed: PropTypes.bool,
  location: PropTypes.shape({}).isRequired,
  epic: PropTypes.shape({
    displayName: PropTypes.string,
  }),
  game: PropTypes.shape({
    name: PropTypes.string,
    _id: PropTypes.string,
    status: PropTypes.string,
    epicUsername: PropTypes.string,
  }).isRequired,
  orderStatus: PropTypes.string.isRequired,
  unlinkEpic: PropTypes.func.isRequired,
  url: PropTypes.string,
  oid: PropTypes.string,
  bid: PropTypes.string,
};

EpicActivate.defaultProps = {
  _id: '',
  oid: '',
  bid: '',
  emailConfirmed: false,
  epic: {},
  url: '',
};

const mapStateToProps = ({
  auth: { _id, epic },
  router: { location },
  epicURL: { url },
}) => ({
  _id,
  epic,
  location,
  url,
});

const mapDispatchToProps = { unlinkEpic };

export default connect(mapStateToProps, mapDispatchToProps)(EpicActivate);
